import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { useAuth } from './useAuth';
import NewDocumentsPanel from './NewDocumentsPanel';
import BulkUpdatePanel from './BulkUpdatePanel';

const ArrowLeftIcon = FaArrowLeft as unknown as React.FC<{ className?: string }>;

const API_BASE = '/api';

interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  children: Category[];
}

interface FlatCategory {
  id: string;
  label: string;
}

function flattenCategories(categories: Category[]): FlatCategory[] {
  const result: FlatCategory[] = [];
  for (const cat of categories) {
    if (cat.children.length === 0) {
      // no sub-categories — this one's directly selectable (Forms, Policies, etc.)
      result.push({ id: cat.id, label: cat.name });
    } else {
      // has sub-categories — only the children are selectable, not the parent itself
      for (const child of cat.children) {
        result.push({ id: child.id, label: `${cat.name} - ${child.name}` });
      }
    }
  }
  return result;
}

function ImportPage() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [batchType, setBatchType] = useState<'new' | 'update'>('new');
  const [flatCategories, setFlatCategories] = useState<FlatCategory[]>([]);
  const [allDocuments, setAllDocuments] = useState<{ id: string; title: string; reference_code: string | null; category_name: string }[]>([]);

  useEffect(() => {
    if (checked && !user) {
      navigate('/admin');
    }
  }, [checked, user, navigate]);

  useEffect(() => {
    fetch(`${API_BASE}/categories`)
      .then((res) => res.json())
      .then((cats: Category[]) => setFlatCategories(flattenCategories(cats)))
      .catch(() => {});

    fetch(`${API_BASE}/documents`, { credentials: 'include' })
      .then((res) => res.json())
      .then((docs) => setAllDocuments(Array.isArray(docs) ? docs : []));
  }, []);

  useEffect(() => {
    if (batchType !== 'update') return;

    fetch(`${API_BASE}/documents`, { credentials: 'include' })
      .then((res) => res.json())
      .then((docs) => setAllDocuments(Array.isArray(docs) ? docs : []));
  }, [batchType]);

  if (!checked || !user) {
    return <div className="app"><p>Checking access...</p></div>;
  }

  return (
    <div className="app">
      <Link to="/" className="back-button">
        <ArrowLeftIcon /> Back to Categories
      </Link>

      <h1>Bulk Import</h1>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${batchType === 'new' ? 'active' : ''}`}
          onClick={() => setBatchType('new')}
        >
          New Documents
        </button>
        <button
          className={`filter-tab ${batchType === 'update' ? 'active' : ''}`}
          onClick={() => setBatchType('update')}
        >
          Bulk Update
        </button>
      </div>

      {batchType === 'new' ? (
        <NewDocumentsPanel flatCategories={flatCategories} />
      ) : (
        <BulkUpdatePanel allDocuments={allDocuments} />
      )}
    </div>
  );
}

export default ImportPage;